/**
 * Learner-added assessments (#444), under Settings → Courses: the upcoming ones first, each on its course tint with a
 * Withdraw pill, then an "Add assessment" pill that opens the form (course, title, kind, day, the sections it covers).
 * A declaration the learner makes, not a conclusion: no status, count, score or percentage appears.
 *
 * The sections come from the course's synced Moodle topics. A course whose topics are not known yet (Moodle not synced,
 * per-learner sync is #497) gets a plain message and no form: the scope cannot be invented. Titles and section names are
 * untrusted text, rendered as plain text with `dir="auto"`.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '@davinci/contracts';
import type { AssessmentCreateRequest, CourseResponse } from '@davinci/contracts';
import { queryKeys } from '../../api/query-keys.js';
import { courseTitle } from '../../app/course-names.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { assessmentDate, useAssessmentLabels, type AssessmentKind } from './assessment-labels.js';

const KINDS: readonly AssessmentKind[] = ['quiz', 'midterm', 'exam', 'other'];

const messages = defineMessages({
  en: {
    heading: 'Assessments',
    intro: 'Tell DaVinci about a quiz, midterm or exam and which sections it covers.',
    upcoming: 'Upcoming',
    none: 'No upcoming assessments.',
    course: 'Assessment course',
    title: 'Title',
    kind: 'Kind',
    date: 'Date',
    sections: 'Sections it covers',
    section: (n: number) => `Section ${String(n)}`,
    add: 'Add assessment',
    cancel: 'Cancel',
    withdraw: 'Withdraw',
    withdrawFor: (title: string) => `Withdraw ${title}`,
    noTopics: "This course's topics are not known yet. They appear once your Moodle is synced.",
    noCourses: 'Select a course above to add an assessment.',
    needSection: 'Pick at least one section.',
    invalid: 'That assessment was not accepted. Check the title, the date and the sections, then try again.',
    notFound: 'That course or assessment is no longer there. Reload the page and try again.',
    loading: 'Loading…',
  },
  he: {
    heading: 'מבחנים',
    intro: 'ספר ל-DaVinci על בוחן, מבחן אמצע או מבחן ועל הפרקים שהוא מכסה.',
    upcoming: 'בקרוב',
    none: 'אין מבחנים קרובים.',
    course: 'קורס המבחן',
    title: 'כותרת',
    kind: 'סוג',
    date: 'תאריך',
    sections: 'הפרקים שהוא מכסה',
    section: (n: number) => `פרק ${String(n)}`,
    add: 'הוספת מבחן',
    cancel: 'לא עכשיו',
    withdraw: 'ביטול',
    withdrawFor: (title: string) => `ביטול ${title}`,
    noTopics: 'הנושאים של הקורס הזה עדיין לא ידועים. הם יופיעו אחרי סנכרון Moodle שלך.',
    noCourses: 'בחר קורס למעלה כדי להוסיף מבחן.',
    needSection: 'בחר לפחות פרק אחד.',
    invalid: 'המבחן לא התקבל. בדוק את הכותרת, התאריך והפרקים ונסה שוב.',
    notFound: 'הקורס או המבחן כבר לא קיימים. טען את הדף מחדש ונסה שוב.',
    loading: 'טוען…',
  },
});

export function AssessmentsSection({ courses }: { readonly courses: readonly CourseResponse[] }): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const queryClient = useQueryClient();
  const { locale } = useLocale();
  const kinds = useAssessmentLabels();
  const [withdrawError, setWithdrawError] = useState<unknown>(undefined);
  const [adding, setAdding] = useState(false);

  const list = useQuery({ queryKey: queryKeys.assessments, queryFn: () => api.getAssessments() });
  const withdraw = useMutation({
    mutationFn: (id: string) => api.withdrawAssessment(id, crypto.randomUUID()),
    onMutate: () => setWithdrawError(undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.assessments }),
    onError: (error) => setWithdrawError(error),
  });
  const name = (ref: string): string => {
    const c = courses.find((x) => x.moodleCourseId === ref);
    return c === undefined ? ref : `${c.code} ${courseTitle(c, locale)}`;
  };

  return (
    <section className="assessments" aria-labelledby="assessments-heading">
      <h2 id="assessments-heading">{t.heading}</h2>
      <p className="field-hint">{t.intro}</p>

      {list.isPending ? <p aria-live="polite">{t.loading}</p> : null}
      {list.isError ? <FailureNotice error={list.error} onRetry={() => void list.refetch()} /> : null}
      {withdrawError !== undefined ? <WriteFailure error={withdrawError} /> : null}
      {list.isSuccess && list.data.assessments.length === 0 ? <p>{t.none}</p> : null}
      {list.isSuccess && list.data.assessments.length > 0 ? (
        <ul className="plain-list assessments-list" aria-label={t.upcoming}>
          {list.data.assessments.map((a) => (
            <li
              key={a.assessmentId}
              className="assessments-row"
              data-course={courses.find((c) => c.moodleCourseId === a.courseRef)?.colour}
            >
              <strong>
                <bdi dir="auto">{a.title}</bdi>
              </strong>
              <span className="assessments-meta">
                {kinds.kind(a.kind)} · {assessmentDate(a.date, locale)} · <bdi dir="auto">{name(a.courseRef)}</bdi>
              </span>
              <button
                type="button"
                className="assessments-withdraw"
                aria-label={t.withdrawFor(a.title)}
                onClick={() => withdraw.mutate(a.assessmentId)}
              >
                {t.withdraw}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {courses.length === 0 ? <p>{t.noCourses}</p> : null}
      {courses.length > 0 && !adding ? (
        <button type="button" className="assessments-add" onClick={() => setAdding(true)}>
          {t.add}
        </button>
      ) : null}
      {courses.length > 0 && adding ? <AddAssessment courses={courses} onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function WriteFailure({ error }: { readonly error: unknown }): React.JSX.Element {
  const t = useMessages(messages);
  if (error instanceof ApiRequestError && error.status === 400) return <p role="alert">{t.invalid}</p>;
  if (error instanceof ApiRequestError && error.status === 404) return <p role="alert">{t.notFound}</p>;
  return <FailureNotice error={error} />;
}

function AddAssessment({
  courses,
  onClose,
}: {
  readonly courses: readonly CourseResponse[];
  readonly onClose: () => void;
}): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const queryClient = useQueryClient();
  const { locale } = useLocale();
  const kinds = useAssessmentLabels();
  const [courseRef, setCourseRef] = useState(courses[0]?.moodleCourseId ?? '');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<AssessmentKind>('exam');
  const [date, setDate] = useState('');
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [showNeed, setShowNeed] = useState(false);

  // A course picked here that has since been unticked falls back to the first one still selected.
  const activeRef = courses.some((c) => c.moodleCourseId === courseRef) ? courseRef : (courses[0]?.moodleCourseId ?? '');
  const topics = useQuery({
    queryKey: queryKeys.courseTopics(activeRef),
    queryFn: () => api.getCourseTopics(activeRef),
    enabled: activeRef !== '',
  });
  const create = useMutation({
    mutationFn: (body: AssessmentCreateRequest) => api.createAssessment(activeRef, crypto.randomUUID(), body),
    onSuccess: () => {
      setTitle('');
      setDate('');
      setPicked([]);
      setShowNeed(false);
      onClose();
      return queryClient.invalidateQueries({ queryKey: queryKeys.assessments });
    },
  });

  const sections = topics.data?.topics ?? [];

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    if (picked.length === 0) {
      setShowNeed(true);
      return;
    }
    create.mutate({ title: title.trim(), kind, date, sectionNumbers: [...picked] });
  }

  function toggle(sectionNumber: string): void {
    setShowNeed(false);
    setPicked((now) => (now.includes(sectionNumber) ? now.filter((n) => n !== sectionNumber) : [...now, sectionNumber]));
  }

  return (
    <form className="assessments-form" onSubmit={submit}>
      <div className="assessments-field">
        <label htmlFor="assessment-course">{t.course}</label>
        <select
          id="assessment-course"
          value={activeRef}
          onChange={(e) => {
            setCourseRef(e.target.value);
            setPicked([]);
          }}
        >
          {courses.map((c) => (
            <option key={c.moodleCourseId} value={c.moodleCourseId}>
              {c.code} {courseTitle(c, locale)}
            </option>
          ))}
        </select>
      </div>

      {topics.isPending ? <p aria-live="polite">{t.loading}</p> : null}
      {topics.isError ? <FailureNotice error={topics.error} onRetry={() => void topics.refetch()} /> : null}
      {topics.isSuccess && sections.length === 0 ? (
        <div className="assessments-actions">
          <p>{t.noTopics}</p>
          <button type="button" className="button-neutral" onClick={onClose}>
            {t.cancel}
          </button>
        </div>
      ) : null}

      {topics.isSuccess && sections.length > 0 ? (
        <>
          <div className="assessments-fields">
            <div className="assessments-field">
              <label htmlFor="assessment-title">{t.title}</label>
              <input
                id="assessment-title"
                type="text"
                required
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="assessments-field">
              <label htmlFor="assessment-kind">{t.kind}</label>
              <select id="assessment-kind" value={kind} onChange={(e) => setKind(e.target.value as AssessmentKind)}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {kinds.kind(k)}
                  </option>
                ))}
              </select>
            </div>
            <div className="assessments-field">
              <label htmlFor="assessment-date">{t.date}</label>
              <input id="assessment-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <fieldset className="assessments-sections">
            <legend>{t.sections}</legend>
            {sections.map((s) => {
              const value = String(s.sectionNumber);
              return (
                <label key={s.topicKey} className="assessments-check">
                  <input type="checkbox" checked={picked.includes(value)} onChange={() => toggle(value)} />
                  <bdi dir="auto">{s.sectionName ?? t.section(s.sectionNumber)}</bdi>
                </label>
              );
            })}
          </fieldset>
          {showNeed ? <p role="alert">{t.needSection}</p> : null}
          {create.isError ? <WriteFailure error={create.error} /> : null}
          <div className="assessments-actions">
            <button type="submit" disabled={create.isPending}>
              {t.add}
            </button>
            <button type="button" className="button-neutral" onClick={onClose}>
              {t.cancel}
            </button>
          </div>
        </>
      ) : null}
    </form>
  );
}
